import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";

const MONTH_DAYS = Array.from({ length: 30 }, (_, i) => i + 1);
const POPOVER_MIN_WIDTH = 288;
const POPOVER_GAP = 8;
const POPOVER_HEIGHT = 292;

function clampDay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(30, Math.max(1, Math.round(n)));
}

export default function MonthDayPicker({
  pickerId = "month-day",
  value,
  onChange,
  disabled = false,
}) {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({
    top: 0,
    left: 0,
    width: POPOVER_MIN_WIDTH,
    placement: "down",
  });
  const selected = clampDay(value);

  const measurePopover = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const width = Math.max(POPOVER_MIN_WIDTH, Math.round(rect.width));
    let left = rect.left;
    const maxLeft = window.innerWidth - width - 8;
    if (left > maxLeft) left = Math.max(8, maxLeft);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openDown = spaceBelow >= POPOVER_HEIGHT || spaceBelow >= rect.top;
    return {
      top: openDown ? rect.bottom + POPOVER_GAP : rect.top - POPOVER_GAP,
      left,
      width,
      placement: openDown ? "down" : "up",
    };
  }, []);

  const updatePopoverPos = useCallback(() => {
    const next = measurePopover();
    if (next) setPopoverPos(next);
  }, [measurePopover]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopoverPos();
    const raf = window.requestAnimationFrame(updatePopoverPos);
    window.addEventListener("resize", updatePopoverPos);
    window.addEventListener("scroll", updatePopoverPos, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePopoverPos);
      window.removeEventListener("scroll", updatePopoverPos, true);
    };
  }, [open, updatePopoverPos]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  function openPicker() {
    if (disabled) return;
    const next = measurePopover();
    if (next) setPopoverPos(next);
    setOpen(true);
  }

  function selectDay(day) {
    onChange(day);
    window.setTimeout(() => setOpen(false), 140);
  }

  const popover =
    typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence>
            {open ? (
              <>
                <motion.button
                  type="button"
                  className="time-picker-dismiss"
                  aria-label="Kapat"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setOpen(false)}
                />
                <div
                  className={`month-day-popover-shell is-${popoverPos.placement}`}
                  style={{
                    top: popoverPos.top,
                    left: popoverPos.left,
                    width: popoverPos.width,
                  }}
                >
                  <motion.div
                    className={`time-picker-popover month-day-popover is-${popoverPos.placement}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Ayın günü"
                    initial={{ opacity: 0, scale: 0.92, y: popoverPos.placement === "down" ? -8 : 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: popoverPos.placement === "down" ? -6 : 6 }}
                    transition={{ type: "spring", duration: 0.32, bounce: 0.16 }}
                  >
                    <div className="time-picker-step-title">Ayın günü</div>
                    <div className="month-day-grid">
                      {MONTH_DAYS.map((day) => {
                        const isSelected = selected === day;
                        return (
                          <button
                            key={day}
                            type="button"
                            className={`time-picker-cell month-day-cell${isSelected ? " is-selected" : ""}`}
                            onClick={() => selectDay(day)}
                          >
                            {isSelected ? (
                              <motion.span
                                layoutId={`month-day-bubble-${pickerId}`}
                                className="time-picker-bubble"
                                transition={{ type: "spring", duration: 0.38, bounce: 0.18 }}
                              />
                            ) : null}
                            <span className="time-picker-cell-label">{day}</span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </div>
              </>
            ) : null}
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`time-picker-trigger input${open ? " is-open" : ""}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openPicker())}
      >
        {selected}
      </button>
      {popover}
    </>
  );
}
